/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { create } from 'zustand';

export interface AntiBotError {
  message: string;
  status?: number;
  /** CHECKBOX site key from the 428 body (D11) -- takes priority over the
   * env-configured ANTI_BOT_CHALLENGE_KEY, which is only a fallback for
   * legacy/degraded responses that don't carry one. */
  siteKey?: string;
  /** Report the failure without offering a checkbox, because solving one cannot
   * help. Set when a challenge was already solved and the retry was refused
   * anyway: re-rendering the widget there produces a puzzle whose token nothing
   * consumes, so the user solves, the overlay closes, and nothing happens. */
  terminal?: boolean;
  /** Why the challenge is terminal, when the generic copy would misdescribe it.
   * `expired` means the request stopped waiting before the person finished --
   * their next attempt can succeed, so telling them to call support is wrong. */
  reason?: 'expired';
  timestamp: number;
}

interface AntiBotState {
  error: AntiBotError | null;
  setError: (error: AntiBotError) => void;
  clearError: () => void;
}

/**
 * Zustand store tracking anti-bot / reCAPTCHA verification errors globally.
 */
export const useAntiBotStore = create<AntiBotState>((set) => ({
  error: null,
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),
}));

/**
 * Report an anti-bot / reCAPTCHA error to the global store.
 * Callable from non-React code (e.g. recaptcha.ts).
 */
export function reportAntiBotError(
  err: unknown,
  status?: number,
  siteKey?: string,
  options?: { terminal?: boolean; reason?: 'expired' },
): void {
  const message = err instanceof Error ? err.message : String(err);
  useAntiBotStore.getState().setError({
    message,
    status,
    siteKey,
    terminal: options?.terminal,
    reason: options?.reason,
    timestamp: Date.now(),
  });
}
